import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox, RadioGroup, Switch, TextBox } from '@progress/kendo-react-inputs';
import { SaveOutline } from '../../../../../icons/icons';
import {
    UDViewDestinationTypeEnum,
    UDViewFlightFilterObjectEnum,
    UDViewPlaneFilterAirlineTypeEnum,
    UDViewPlaneFilterSettingModel,
    UDViewTagTextColor
} from '../../../../../udView/udview';
import ColorPickerWithDefault from '../../../../components/inputs/ColorPickerWithDefault';
import { DropdownList, DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import { useAppDispatch } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import {
    RadioButtonList,
    useFormColorValue,
    useFormDropdownValue,
    useFormNumericValue,
    useFormRadioValue,
    useFormStringValue,
    useFormSwitchValue,
    useFormUpperCaseStringValue
} from '../../hooks/forms';

export const DEFAULT_FILTER_COLOR = '#5585F5';
export function FilterFlyoutEdit({
    edit,
    close,
    template
}: {
    edit?: { filter: Partial<UDViewPlaneFilterSettingModel> | null; index: number } | null;
    close: () => void;
    template?: Partial<UDViewPlaneFilterSettingModel> | null;
}) {
    const filter = edit?.filter ?? template;
    const color = useFormColorValue(filter?.color ?? DEFAULT_FILTER_COLOR);
    const name = useFormStringValue(filter?.name ?? '');
    const myFleet = useFormSwitchValue(filter?.useMyFleet ?? false);
    const acid = useFormUpperCaseStringValue(filter?.acid ?? '*');
    const destinationType = useFormDropdownValue(
        AirportModeOptions,
        filter?.destinationType ?? UDViewDestinationTypeEnum.OriginToDestination
    );
    const origin = useFormUpperCaseStringValue(filter?.origin ?? '');
    const destination = useFormUpperCaseStringValue(filter?.destination ?? '');
    const acType = useFormUpperCaseStringValue(filter?.aircraftType ?? '');
    const regId = useFormUpperCaseStringValue(filter?.tail ?? '');
    const beacon = useFormUpperCaseStringValue(filter?.beaconCode ?? '');
    const icao = useFormUpperCaseStringValue(filter?.icaoCode ?? '');
    const speedLower = useFormNumericValue(filter?.speedLower ?? null);
    const speedUpper = useFormNumericValue(filter?.speedUpper ?? null);
    const altitudeLower = useFormNumericValue(filter?.altitudeLower ?? null);
    const altitudeUpper = useFormNumericValue(filter?.altitudeUpper ?? null);
    const sector = useFormUpperCaseStringValue(filter?.sectorDeskId ?? '');
    const distanceToDest = useFormNumericValue(filter?.distanceToDestination ?? null);
    const acClass = useFormDropdownValue(FilterObjectOptions, filter?.filterObjectType ?? UDViewFlightFilterObjectEnum.All);
    const holding = useFormSwitchValue(filter?.holding ?? false);
    const diverting = useFormSwitchValue(filter?.diverted ?? false);
    const history = useFormSwitchValue(filter?.showHistory ?? false);
    const tags = useFormSwitchValue(filter?.showTags ?? false);
    const tagColor = useFormRadioValue<UDViewTagTextColor>(ViewTagTextColorOptions, filter?.tagTextColor ?? UDViewTagTextColor.default);

    const dispatch = useAppDispatch();
    const handleSave = () => {
        dispatch(
            patchFocusedWorkspaceView((view) => {
                const filter: UDViewPlaneFilterSettingModel = {
                    name: name.value || autoGeneratedName(),
                    color: color.value,
                    isActive: true,
                    include: true,
                    airlineType: UDViewPlaneFilterAirlineTypeEnum.SpecificAirlineId,
                    useMyFleet: myFleet.checked,
                    acid: acid.value,
                    origin: origin.value,
                    destination: destination.value,
                    destinationType: destinationType.value,
                    useAltAirport: false,
                    beaconCode: beacon.value,
                    icaoCode: icao.value,
                    altitudeLower: altitudeLower.value,
                    altitudeUpper: altitudeUpper.value,
                    speedLower: speedLower.value,
                    speedUpper: speedUpper.value,
                    aircraftType: acType.value,
                    filterObjectType: acClass.value,
                    timeBefore: null,
                    timeAfter: null,
                    timeType: null,
                    distanceToDestination: distanceToDest.value,
                    sectorDeskId: sector.value,
                    tail: regId.value,
                    diverted: diverting.checked,
                    holding: holding.checked,
                    showTags: tags.checked,
                    showHistory: history.checked,
                    showHolding: holding.checked,
                    showDiverting: diverting.checked,
                    tagTextColor: tagColor.value
                };
                if (edit) {
                    view.plane.filter.planeFilterSettings[edit.index] = filter;
                } else {
                    view.plane.filter.planeFilterSettings.push(filter);
                    if (view.plane.filter.planeFilterSettings.length === 1) {
                        view.plane.filter.useFilters = true;
                    }
                }
            })
        );
        close();
    };

    const autoGeneratedName = (): string => {
        if (myFleet.checked) {
            return 'My fleet';
        }

        if (acid.value) {
            return acid.value.toUpperCase();
        }

        if (origin.value || destination.value) {
            if (destinationType.value === UDViewDestinationTypeEnum.OriginToDestination) {
                const str = (origin.value.trim() ? origin.value : '*') + ' -> ' + (destination.value.trim() ? destination.value : '*');
                return str.toUpperCase();
            }

            const airportId = origin.value.trim() ? origin.value : '*';
            return AirportModeOptions.find((x) => x.value === destinationType.value)!.text! + ' ' + airportId.toUpperCase();
        }

        if (acType.value.trim()) {
            return acType.value.toUpperCase();
        }

        if (regId.value.trim()) {
            return regId.value.toUpperCase();
        }

        if (beacon.value.trim()) {
            return beacon.value.toUpperCase();
        }

        if (icao.value.trim()) {
            return icao.value.toUpperCase();
        }

        if (speedUpper.value != null && speedLower.value != null) {
            return `${speedLower.value}kts < speed < ${speedUpper.value}kts`;
        }

        if (speedUpper.value != null) {
            return `speed < ${speedUpper.value}kts`;
        }

        if (speedLower.value != null) {
            return `speed > ${speedLower.value}kts`;
        }

        if (altitudeUpper.value != null && altitudeLower.value != null) {
            return `${altitudeLower.value}ft < altitude < ${altitudeUpper.value}ft`;
        }

        if (altitudeUpper.value != null) {
            return `altitude < ${altitudeUpper.value}ft`;
        }

        if (altitudeLower.value != null) {
            return `altitude > ${altitudeLower.value}ft`;
        }

        if (sector.value.trim()) {
            return sector.value.toUpperCase();
        }

        if (distanceToDest.value != null) {
            return `Distance < ${distanceToDest.value} miles`;
        }

        return FilterObjectOptions.find((x) => x.value === acClass.value)?.text ?? '';
    };

    return (
        <div className="h-100 min-h-0 d-flex flex-column" style={{ maxWidth: 360 }}>
            <div className="pt-2 h-100 min-h-0 overflow-auto">
                <div
                    className="gap-2 px-3"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content 1fr',
                        alignItems: 'center',
                        justifyItems: 'end'
                    }}>
                    <div className="d-flex gap-2 align-items-center">
                        Name
                        <div>
                            <ColorPickerWithDefault defaultColor={DEFAULT_FILTER_COLOR} id="filter-color" {...color} />
                        </div>
                    </div>
                    <TextBox id="filter-name" {...name} className="flex-grow-1" autoFocus />

                    <div>My fleet</div>
                    <Switch id="filter-my-fleet-select" {...myFleet} />

                    <div>Callsign/ACID</div>
                    <TextBox id="filter-acid" {...acid} />

                    <div>Airport</div>
                    <DropdownListWithValueField {...destinationType} />

                    {destinationType.value !== UDViewDestinationTypeEnum.BlankOriginOrDestination && (
                        <>
                            <div>{destinationType.value === UDViewDestinationTypeEnum.OriginToDestination ? 'Origin' : 'Airport ID'}</div>
                            <TextBox id="filter-origin" {...origin} />

                            {destinationType.value === UDViewDestinationTypeEnum.OriginToDestination && (
                                <>
                                    <div>Destination</div>
                                    <TextBox id="filter-destination" {...destination} />
                                </>
                            )}
                        </>
                    )}

                    <div>Aircraft type</div>
                    <TextBox {...acType} />

                    <div>Registration #</div>
                    <TextBox {...regId} />

                    <div>Beacon code</div>
                    <TextBox {...beacon} />

                    <div>ICAO code:</div>
                    <TextBox {...icao} />

                    <div className="d-flex gap-2 align-items-center" style={{ gridColumn: '1/3' }}>
                        <div>Ground speed</div>
                        <NumericTextBox {...speedLower} format="0 kts" min={0} />
                        <div>to</div>
                        <NumericTextBox {...speedUpper} format="0 kts" min={speedLower.value ? Math.max(speedLower.value - 1, 0) : 0} />
                    </div>

                    <div className="d-flex gap-2 align-items-center" style={{ gridColumn: '1/3' }}>
                        <div>Altitude</div>
                        <NumericTextBox {...altitudeLower} step={100} format={'0 ft'} />
                        <div>to</div>
                        <NumericTextBox {...altitudeUpper} step={100} format={'0 ft'} />
                    </div>

                    <div>Sector/desk ID</div>
                    <TextBox {...sector} />

                    <div>
                        Distance to
                        <br />
                        destination
                    </div>
                    <NumericTextBox {...distanceToDest} format={'0 miles'} min={1} />

                    <div>Class</div>
                    <DropdownListWithValueField {...acClass} />

                    <div>Holding</div>
                    <Switch {...holding} />

                    <div>Diverting</div>
                    <Switch {...diverting} />

                    <div>Show history</div>
                    <Switch {...history} />

                    <div>Show tags</div>
                    <Switch id="filter-show-tags" {...tags} />

                    <div>Tag color</div>
                    <RadioGroup className="d-inline-flex" {...tagColor} layout="horizontal" />
                </div>
            </div>

            <div className="bg-color py-2 mt-2 z-2 border-top">
                <div className="d-flex gap-2 px-3">
                    <Button className="flex-grow-1" fillMode={'outline'} onClick={close}>
                        Cancel
                    </Button>
                    <Button id="filter-save" className="flex-grow-1" themeColor={'primary'} svgIcon={SaveOutline} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
}

const AirportModeOptions: DropdownList<UDViewDestinationTypeEnum> = [
    { value: UDViewDestinationTypeEnum.OriginToDestination, text: 'Origin to destination' },
    { value: UDViewDestinationTypeEnum.Destination, text: 'Destination' },
    { value: UDViewDestinationTypeEnum.OriginOrDestination, text: 'Origin or destination' },
    { value: UDViewDestinationTypeEnum.DivertingFrom, text: 'Diverting from' },
    { value: UDViewDestinationTypeEnum.DivertingTo, text: 'Diverting to' },
    { value: UDViewDestinationTypeEnum.Alternate, text: 'Alternate' },
    { value: UDViewDestinationTypeEnum.BlankOriginOrDestination, text: 'Blank origin or destination' }
];

const FilterObjectOptions: DropdownList<UDViewFlightFilterObjectEnum> = [
    { value: UDViewFlightFilterObjectEnum.None, text: 'None' },
    { value: UDViewFlightFilterObjectEnum.Aircraft, text: 'Aircraft' },
    { value: UDViewFlightFilterObjectEnum.GroundVehicle, text: 'Ground vehicle' },
    { value: UDViewFlightFilterObjectEnum.All, text: 'All' }
];

const ViewTagTextColorOptions: RadioButtonList<UDViewTagTextColor> = [
    { value: UDViewTagTextColor.default, label: 'Default' },
    { value: UDViewTagTextColor.sameAsPlane, label: 'Same as plane' }
];
